import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { Outlet } from '@tanstack/react-router';
import {
	type FileTypeDTO,
	getGetAllDocumentsQueryKey,
	useAddDocumentToOrganization,
} from '@uturn/api/v1';
import {
	ALLOWED_FILE_EXTENSIONS,
	DROPZONE_ACCEPT_MAP,
	FileDropzone,
	MAX_DESCRIPTION_CHARACTERS,
	MAX_UPLOAD_FILE_SIZE_BYTES,
	sanitizeFileName,
	validateDocumentFileName,
} from '@uturn/portal/components/file-dropzone';
import { MetaDataContext } from '@uturn/portal/context';
import { queryClient } from '@uturn/portal/query';
import { getApiErrorMessage } from '@uturn/portal/utils';
import {
	Button,
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Separator,
	sonner,
} from '@uturn/ui-kit';
import { useContext, useMemo, useState } from 'react';
import Dropzone, { type FileRejection } from 'react-dropzone';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
	type DocumentUploadFormValues,
	documentUploadRequiredFields,
	documentUploadSchema,
} from '../schema';

// Generated `FileTypeDTOObjectType` enum is incomplete; runtime data includes 'PARTY'.
const isPartyObjectType = (value: unknown): value is 'PARTY' =>
	value === 'PARTY';

type PartyFileType = FileTypeDTO & { code: string; name: string };

export function OrganizationDocumentsLayout() {
	const { shipmentMetaData } = useContext(MetaDataContext);
	const { t } = useTranslation();
	const [openModal, setOpenModal] = useState(false);

	const partyFileTypes = useMemo<PartyFileType[]>(
		() =>
			(shipmentMetaData?.fileTypes ?? []).filter(
				(fileType): fileType is PartyFileType =>
					fileType.code !== undefined &&
					fileType.name !== undefined &&
					isPartyObjectType(fileType.objectType),
			),
		[shipmentMetaData?.fileTypes],
	);

	const form = useForm<DocumentUploadFormValues>({
		resolver: zodResolver(documentUploadSchema),
		mode: 'onChange',
		defaultValues: {
			typeCode: '',
			description: '',
		},
	});

	const { mutate, isPending } = useAddDocumentToOrganization({
		mutation: {
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: getGetAllDocumentsQueryKey(),
				});
				form.reset();
				setOpenModal(false);
				sonner.success(
					t(
						'pages.organisation.documents.form.submit.success.description',
						'Your document has been uploaded',
					),
				);
			},
			onError: (error) => {
				sonner.error(
					t(
						'pages.organisation.documents.form.submit.error.description',
						'Your document has not been uploaded',
					),
					{ description: getApiErrorMessage(error) },
				);
			},
		},
	});

	const onSubmit = (values: DocumentUploadFormValues) => {
		const fileType = partyFileTypes.find(
			(type) => type.code === values.typeCode,
		);
		if (fileType === undefined) {
			return;
		}

		mutate({
			data: {
				file: values.file,
				properties: {
					description: values.description,
					fileName: values.file.name,
					type: fileType,
				},
			},
		});
	};

	// Mirror the shipment add-document validation
	const handleFileDrop = (acceptedFiles: File[]) => {
		const [selectedFile] = acceptedFiles;
		if (selectedFile === undefined) {
			return;
		}

		const validationError = validateDocumentFileName(selectedFile.name);
		if (validationError !== null) {
			form.setError('file', {
				type: 'manual',
				message: t(
					validationError.key,
					validationError.defaultMessage,
					validationError.params,
				),
			});
			return;
		}

		const normalizedFile = new File(
			[selectedFile],
			sanitizeFileName(selectedFile.name),
			{ type: selectedFile.type, lastModified: selectedFile.lastModified },
		);
		form.setValue('file', normalizedFile, { shouldValidate: true });
	};

	const handleFileDropRejected = (rejections: FileRejection[]) => {
		const firstError = rejections[0]?.errors?.[0];

		let message = t('pages.adddocument.file-rejected-error', 'File rejected.');
		if (firstError?.code === 'file-too-large') {
			message = t(
				'pages.adddocument.file-too-large-error',
				'File must be 5 MB or smaller.',
			);
		} else if (firstError?.code === 'file-invalid-type') {
			message = t(
				'pages.adddocument.file-invalid-type-error',
				'Invalid file extension. Allowed: {{allowed}}.',
				{ allowed: ALLOWED_FILE_EXTENSIONS.join(', ') },
			);
		}

		form.setError('file', { type: 'manual', message });
	};

	return (
		<>
			<Helmet
				title={t(
					'pages.account.organisation.documents.title',
					'Organisation - Documents',
				).toString()}
			/>
			<div className="space-y-6">
				<div className="flex items-center justify-between space-y-2">
					<div>
						<h3 className="text-lg font-medium">
							{t('pages.account.organisation.documents.title', 'Documents')}
						</h3>
						<p className="text-sm text-muted-foreground">
							{t(
								'pages.account.organisation.documents.description',
								'Manage the documents in your organisation',
							)}
						</p>
					</div>
					<div className="flex items-center space-x-2">
						<Dialog
							open={openModal}
							onOpenChange={(open) => {
								if (!open) {
									form.reset();
								}
								setOpenModal(open);
							}}
						>
							<DialogTrigger asChild>
								<Button>
									{t(
										'pages.organisation.documents.actions.add',
										'Add new document',
									)}
								</Button>
							</DialogTrigger>
							<DialogContent className="sm:max-w-[425px]">
								<DialogHeader>
									<DialogTitle>
										{t('pages.adddocument.pagetitle', 'Document upload')}
									</DialogTitle>
									<DialogDescription>
										{t('general.text', 'Text')}
									</DialogDescription>
								</DialogHeader>
								<Form {...form}>
									<form onSubmit={form.handleSubmit(onSubmit)}>
										<div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:max-w-xl sm:grid-cols-6">
											<div className="col-span-full">
												<FormField
													control={form.control}
													name="typeCode"
													render={({ field }) => (
														<FormItem>
															<FormLabel
																className={
																	documentUploadRequiredFields.typeCode
																		? 'required-asterix'
																		: ''
																}
															>
																{t('pages.adddocument.formlabels.type', 'Type')}
															</FormLabel>
															<Select
																onValueChange={field.onChange}
																value={field.value}
															>
																<FormControl>
																	<SelectTrigger className="w-full">
																		<SelectValue
																			placeholder={
																				<span>
																					{t(
																						'pages.adddocument.formlabels.type-placeholder',
																						'Select a file type',
																					)}
																				</span>
																			}
																		/>
																	</SelectTrigger>
																</FormControl>
																<SelectContent>
																	{partyFileTypes.map((fileType) => (
																		<SelectItem
																			key={fileType.code}
																			value={fileType.code}
																		>
																			{fileType.name}
																		</SelectItem>
																	))}
																</SelectContent>
															</Select>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="col-span-full">
												<FormField
													control={form.control}
													name="description"
													render={({ field }) => (
														<FormItem>
															<FormLabel
																className={
																	documentUploadRequiredFields.description
																		? 'required-asterix'
																		: ''
																}
															>
																{t(
																	'pages.adddocument.formlabels.description',
																	'Description',
																)}
															</FormLabel>
															<FormControl>
																<Input
																	{...field}
																	maxLength={MAX_DESCRIPTION_CHARACTERS}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="col-span-full">
												<FormField
													control={form.control}
													name="file"
													render={({ field }) => (
														<FormItem>
															<FormControl>
																{field.value ? (
																	<section className="max-w-xl">
																		<div className="bg-secondary-50/20 border-secondary-300 flex h-40 w-full appearance-none items-center justify-center rounded-md border-2 border-dashed p-4 transition focus:outline-none">
																			<div className="flex items-center">
																				<span className="text-sm">
																					{field.value.name}
																				</span>
																				<Button
																					type="button"
																					variant="ghost"
																					onClick={() =>
																						field.onChange(undefined)
																					}
																				>
																					<Icon
																						icon={faTrashCan}
																						className="-ml-1 size-4 pt-0.5"
																					/>
																				</Button>
																			</div>
																		</div>
																	</section>
																) : (
																	<Dropzone
																		onDrop={handleFileDrop}
																		onDropRejected={handleFileDropRejected}
																		maxSize={MAX_UPLOAD_FILE_SIZE_BYTES}
																		accept={DROPZONE_ACCEPT_MAP}
																	>
																		{({ getRootProps, getInputProps }) => (
																			<FileDropzone
																				getInputProps={getInputProps}
																				getRootProps={getRootProps}
																			/>
																		)}
																	</Dropzone>
																)}
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="col-span-full">
												<DialogFooter>
													<DialogClose asChild>
														<Button type="button" variant="outline">
															{t('general.cancel', 'Cancel')}
														</Button>
													</DialogClose>
													<Button
														type="submit"
														disabled={!form.formState.isValid || isPending}
													>
														{t('pages.adddocument.submit', 'Save document')}
													</Button>
												</DialogFooter>
											</div>
										</div>
									</form>
								</Form>
							</DialogContent>
						</Dialog>
					</div>
				</div>
				<Separator />
				<Outlet />
			</div>
		</>
	);
}
